/**
 * Trash Page
 * View and restore deleted projects
 * Issue #159
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import type { Project } from '@veodiagram/shared';
import { api } from '../services/api';

export function TrashPage() {
  const { isAuthenticated } = useAuthStore();
  const [deletedProjects, setDeletedProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadDeletedProjects();
    }
  }, [isAuthenticated]);

  const loadDeletedProjects = async () => {
    try {
      const data = await api.getDeletedProjects();
      setDeletedProjects(data);
    } catch (err) {
      console.error('Failed to load deleted projects:', err);
      setError('Failed to load trash');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async (projectId: string) => {
    setRestoringId(projectId);
    setError(null);
    try {
      await api.restoreProject(projectId);
      setDeletedProjects(deletedProjects.filter(p => p.id !== projectId));
    } catch (err) {
      console.error('Failed to restore project:', err);
      setError('Failed to restore project');
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async (projectId: string) => {
    if (!confirm('Are you sure you want to permanently delete this project? This action cannot be undone.')) {
      return;
    }
    setDeletingId(projectId);
    setError(null);
    try {
      await api.deleteProject(projectId, true);
      setDeletedProjects(deletedProjects.filter(p => p.id !== projectId));
    } catch (err) {
      console.error('Failed to permanently delete project:', err);
      setError('Failed to delete project');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEmptyTrash = async () => {
    if (!confirm('Are you sure you want to permanently delete all items in trash? This action cannot be undone.')) {
      return;
    }
    setEmptyingTrash(true);
    setError(null);
    try {
      await api.emptyProjectTrash();
      setDeletedProjects([]);
    } catch (err) {
      console.error('Failed to empty trash:', err);
      setError('Failed to empty trash');
    } finally {
      setEmptyingTrash(false);
    }
  };

  // Calculate days until permanent deletion
  const getDaysRemaining = (deletedAt: string | Date | null | undefined): number => {
    if (!deletedAt) return 30;
    const deleteDate = new Date(deletedAt);
    const expiryDate = new Date(deleteDate.getTime() + 30 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center text-muted-foreground">
          Please sign in to view your trash.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Link
          to="/dashboard"
          className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-block"
        >
          &larr; Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <h1 className="text-3xl font-bold">Trash</h1>
          </div>
          {deletedProjects.length > 0 && (
            <button
              onClick={handleEmptyTrash}
              disabled={emptyingTrash}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
            >
              {emptyingTrash ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Emptying...
                </>
              ) : (
                'Empty Trash'
              )}
            </button>
          )}
        </div>
        <p className="text-muted-foreground mt-2">
          Items in trash will be permanently deleted after 30 days.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading trash...</p>
        </div>
      ) : deletedProjects.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          <p className="text-muted-foreground">Trash is empty</p>
        </div>
      ) : (
        <div className="space-y-4">
          {deletedProjects.map((project) => {
            const daysRemaining = getDaysRemaining(project.deletedAt);
            const isRestoring = restoringId === project.id;
            const isDeleting = deletingId === project.id;

            return (
              <div
                key={project.id}
                className="p-4 border border-border rounded-lg bg-muted/30"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-muted-foreground">{project.name}</h3>
                      {project.sourceType === 'local' ? (
                        <span className="text-xs bg-muted px-2 py-0.5 rounded">Local</span>
                      ) : (
                        <span className="text-xs bg-muted px-2 py-0.5 rounded">GitHub</span>
                      )}
                    </div>
                    {project.description && (
                      <p className="text-sm text-muted-foreground mt-1">{project.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      {daysRemaining > 0 ? (
                        <>Permanently deleted in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</>
                      ) : (
                        <span className="text-red-500">Scheduled for permanent deletion</span>
                      )}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleRestore(project.id)}
                      disabled={isRestoring || isDeleting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/10 transition-colors disabled:opacity-50"
                    >
                      {isRestoring ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Restoring...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Restore
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handlePermanentDelete(project.id)}
                      disabled={isRestoring || isDeleting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Delete Forever
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
